import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiLogIn, FiArrowRight, FiPercent, FiUserPlus, FiBook } from 'react-icons/fi';
import { FaWhatsapp, FaGraduationCap } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id) => {
    setIsOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          window.scrollTo({ top: element.offsetTop - 80, behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        window.scrollTo({ top: element.offsetTop - 80, behavior: "smooth" });
      }
    }
  };

  const navLinks = [
    { name: "Cursos", path: "/shop" },
    { name: "Salas 24/7", path: "/salas" },
    { name: "Precios", path: "/pricing" },
    { name: "Blog", path: "/blog" },
    { name: "Inscribirse", path: "/checkout" }
  ];

  const campusLink = "https://app.onlineclassesenglish.com";
  const whatsappNumber = "+529844594738";

  return (
    <header className={`fixed w-full top-0 z-[100] transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md border-b border-green-900' : 'bg-black/50 backdrop-blur-sm'}`}>
      <div className="bg-gradient-to-r from-red-600 via-yellow-500 to-green-600 h-8 flex items-center justify-center text-[10px] md:text-xs font-black text-black uppercase tracking-widest gap-2">
        <span>Universidad Online 24/7</span>
        <span className="hidden md:inline">•</span>
        <span>Maestros Nativos</span>
        <span className="hidden md:inline">•</span>
        <a href={campusLink} className="underline hover:text-white transition-colors flex items-center gap-1"> Lobby del Campus <FiArrowRight /> </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-zinc-900 to-black border-2 border-yellow-500/50 rounded-full flex items-center justify-center text-yellow-500 group-hover:bg-yellow-500 group-hover:text-black transition-all duration-300 shadow-lg relative">
            <SafeIcon icon={FaGraduationCap} className="text-2xl" />
          </div>
          <div className="flex flex-col">
            <span className="font-black text-lg md:text-xl tracking-tighter text-white leading-none uppercase">
              ONLINE<span className="text-red-600">CLASSES</span><span className="text-green-600">ENGLISH</span>
            </span>
            <span className="text-[10px] text-gray-400 tracking-widest uppercase font-bold">Universidad Online</span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 shrink-0">
          {navLinks.map((link) => (
            link.path ? (
              <Link key={link.name} to={link.path} className="font-bold text-sm uppercase tracking-widest text-gray-300 hover:text-yellow-500 transition-colors">
                {link.name}
              </Link>
            ) : (
              <button key={link.name} onClick={() => handleNavClick(link.id)} className="font-bold text-sm uppercase tracking-widest text-gray-300 hover:text-yellow-500 transition-colors">
                {link.name}
              </button>
            )
          ))}
          <div className="h-6 w-px bg-zinc-700 mx-2"></div>
          <a href={`https://wa.me/${whatsappNumber.replace('+', '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-green-500 font-bold text-sm uppercase tracking-widest hover:text-green-400">
            <SafeIcon icon={FaWhatsapp} className="text-xl" /> WhatsApp
          </a>
          <a href={campusLink} className="bg-yellow-500 text-black px-6 py-2.5 rounded-full font-black text-xs uppercase tracking-widest hover:bg-yellow-400 transition-all hover:scale-105 shadow-lg flex items-center gap-2">
            <SafeIcon icon={FiLogIn} /> Entrar al Campus
          </a>
        </nav>

        <button className="xl:hidden text-3xl text-yellow-500" onClick={() => setIsOpen(!isOpen)}>
          <SafeIcon icon={isOpen ? FiX : FiMenu} />
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} className="fixed inset-0 bg-black z-50 p-6 flex flex-col">
            <div className="flex justify-end mb-12">
              <button onClick={() => setIsOpen(false)} className="text-4xl text-yellow-500"><FiX /></button>
            </div>
            <div className="flex flex-col gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => {
                    if (link.path) navigate(link.path);
                    else handleNavClick(link.id);
                    setIsOpen(false);
                  }}
                  className="text-4xl font-black text-white uppercase text-left flex items-center justify-between group"
                >
                  {link.name}
                  <SafeIcon icon={FiArrowRight} className="text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
            <div className="mt-auto space-y-4">
              <Link to="/checkout" onClick={() => setIsOpen(false)} className="w-full bg-red-600 text-white py-5 rounded-2xl font-black text-center block uppercase tracking-widest flex items-center justify-center gap-3">
                <SafeIcon icon={FiUserPlus} /> Inscribirse Ahora
              </Link>
              <a href={campusLink} className="w-full bg-yellow-500 text-black py-5 rounded-2xl font-black text-center block uppercase tracking-widest flex items-center justify-center gap-3">
                <SafeIcon icon={FiLogIn} /> Entrar al Campus
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;